import { Message } from '@app/app/_components/Message/Message';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { useTranslations } from 'next-intl';
import { PolicyValidity } from '@app/domain/model/eshop/transport/PolicyValidity';
import { format } from 'date-fns';
import { InsurancePackage } from '@app/domain/model/eshop/transport/InsurancePackage';
import { InsuranceDuration } from '@app/domain/model/eshop/transport/InsuranceDuration';

interface Props {
    status: InsurancePackage;
    cascoValidity: PolicyValidity | null;
    mtplValidity: PolicyValidity | null;
    isCascoPresent?: boolean;
    insuranceDuration?: string;
}

export function TransportInsuranceMessages({
    status,
    cascoValidity,
    mtplValidity,
    isCascoPresent = true,
    insuranceDuration,
}: Props) {
    const t = useTranslations();

    if (status === InsurancePackage.noPremiumsAvailable) {
        return (
            <Message className="md:flex-row md:items-start" variant="warning">
                <div className="flex flex-col gap-2">
                    <Paragraph isBold>{t('eshop.transport.noPremiumsAvailable')}</Paragraph>
                    <Paragraph size="small">{t('eshop.transport.noPremiumsAvailableDescription')}</Paragraph>
                </div>
            </Message>
        );
    }

    if (status === InsurancePackage.cascoInvalidMtplValidLongTerm && mtplValidity) {
        return (
            <Message className="md:flex-row md:items-start" variant="warning">
                <div className="flex flex-col gap-2">
                    <Paragraph
                        isBold>{`${t('eshop.transport.validMtplLongTerm')} ${format(mtplValidity.validUntil ?? '', 'yyyy-MM-dd')}`}</Paragraph>
                    <Paragraph size="small">
                        {isCascoPresent
                            ? t('eshop.transport.validMtplLongTermDescription')
                            : t('eshop.transport.selectTwelveMonthsForCascoMessage')}
                    </Paragraph>
                </div>
            </Message>
        );
    }

    if (status === InsurancePackage.mtplInvalidCascoValidLongTerm && cascoValidity) {
        return (
            <Message className="md:flex-row md:items-start" variant="warning">
                <div className="flex flex-col gap-2">
                    {isCascoPresent && (
                        <Paragraph isBold>{t('eshop.transport.youHaveActiveInsurancePolicy')}</Paragraph>
                    )}
                    {isCascoPresent ? (
                        <ul>
                            <li className="ml-5 list-disc">
                                <Paragraph size="small">{`${t('eshop.transport.cascoInsuranceValidUntil')}: ${format(cascoValidity.validUntil ?? '', 'yyyy-MM-dd')}`}</Paragraph>
                            </li>
                            <li className="ml-5 list-disc">
                                <Paragraph size="small">{t('eshop.transport.newPolicyCanBeSigned')}</Paragraph>
                            </li>
                        </ul>
                    ) : (
                        <Paragraph size="small">{t('eshop.transport.selectTwelveMonthsForCascoMessage')}</Paragraph>
                    )}
                </div>
            </Message>
        );
    }

    if (status === InsurancePackage.mtplValidExpiringSoonCascoInvalid && mtplValidity) {
        return (
            <Message className="md:flex-row md:items-start" variant="info">
                <div className="flex flex-col gap-2">
                    <Paragraph isBold>{t('eshop.transport.youHaveActiveMtplPolicy')}</Paragraph>
                    <ul>
                        <li className="ml-5 list-disc">
                            <Paragraph size="small">{`${t('eshop.transport.policyExpiresMTPL')} ${format(mtplValidity.validUntil ?? '', 'yyyy-MM-dd')}`}</Paragraph>
                        </li>
                        <li className="ml-5 list-disc">
                            <Paragraph size="small">
                                {isCascoPresent
                                    ? t('eshop.transport.additionalCascoOptionsAvailable')
                                    : t('eshop.transport.selectTwelveMonthsForCascoMessage')}
                            </Paragraph>
                        </li>
                    </ul>
                </div>
            </Message>
        );
    }

    if (status === InsurancePackage.cascoValidExpiringSoonMtplInvalid && cascoValidity) {
        return (
            <Message className="md:flex-row md:items-start" variant="info">
                <div className="flex flex-col gap-2">
                    {isCascoPresent && <Paragraph isBold>{t('eshop.transport.youHaveAnActiveCascoPolicy')}</Paragraph>}
                    {isCascoPresent ? (
                        <ul>
                            <li className="ml-5 list-disc">
                                <Paragraph size="small">{`${t('eshop.transport.policyExpiresCASCO')} ${format(cascoValidity.validUntil ?? '', 'yyyy-MM-dd')}`}</Paragraph>
                            </li>
                            <li className="ml-5 list-disc">
                                <Paragraph size="small">{t('eshop.transport.renewalAvailable')}</Paragraph>
                            </li>
                        </ul>
                    ) : (
                        <Paragraph size="small">{t('eshop.transport.selectTwelveMonthsForCascoMessage')}</Paragraph>
                    )}
                </div>
            </Message>
        );
    }

    if (status === InsurancePackage.cascoMtplValidLongTerm && cascoValidity && mtplValidity) {
        return (
            <Message className="md:flex-row md:items-start" variant="warning">
                <div className="flex flex-col gap-2">
                    <Paragraph isBold>{t('eshop.transport.youHaveActiveInsurancePolicies')}</Paragraph>
                    {isCascoPresent ? (
                        <ul>
                            <li className="ml-5 list-disc">
                                <Paragraph size="small">{`${t('eshop.transport.cascoInsuranceValidUntil')} ${format(cascoValidity.validUntil ?? '', 'yyyy-MM-dd')}`}</Paragraph>
                            </li>
                            <li className="ml-5 list-disc">
                                <Paragraph size="small">{`${t('eshop.transport.mtplInsuranceValidUntil')} ${format(mtplValidity.validUntil ?? '', 'yyyy-MM-dd')}`}</Paragraph>
                            </li>
                            <li className="ml-5 list-disc">
                                <Paragraph size="small">{t('eshop.transport.newPolicyCanBeSigned')}</Paragraph>
                            </li>
                        </ul>
                    ) : (
                        <ul>
                            <li className="ml-5 list-disc">
                                <Paragraph size="small">{`${t('eshop.transport.mtplInsuranceValidUntil')} ${format(mtplValidity.validUntil ?? '', 'yyyy-MM-dd')}`}</Paragraph>
                            </li>
                            <li className="ml-5 list-disc">
                                <Paragraph size="small">
                                    {t('eshop.transport.selectTwelveMonthsForCascoMessage')}
                                </Paragraph>
                            </li>
                        </ul>
                    )}
                </div>
            </Message>
        );
    }

    if (
        (status === InsurancePackage.bothValidCascoExpiringSoon ||
            status === InsurancePackage.bothValidMtplExpiringSoon ||
            status === InsurancePackage.cascoMtplValidExpiringSoon) &&
        cascoValidity &&
        mtplValidity
    ) {
        return (
            <Message className="md:flex-row md:items-start" variant="info">
                <div className="flex flex-col gap-2">
                    <Paragraph isBold>{t('eshop.transport.youHaveActiveInsurancePolicies')}</Paragraph>
                    {isCascoPresent ? (
                        <ul>
                            <li className="ml-5 list-disc">
                                <Paragraph size="small">{`${t('eshop.transport.cascoInsuranceValidUntil')} ${format(cascoValidity.validUntil ?? '', 'yyyy-MM-dd')}`}</Paragraph>
                            </li>
                            <li className="ml-5 list-disc">
                                <Paragraph size="small">{`${t('eshop.transport.mtplInsuranceValidUntil')} ${format(mtplValidity.validUntil ?? '', 'yyyy-MM-dd')}`}</Paragraph>
                            </li>
                            <li className="ml-5 list-disc">
                                <Paragraph size="small">{t('eshop.transport.renewalAvailable')}</Paragraph>
                            </li>
                        </ul>
                    ) : (
                        <ul>
                            <li className="ml-5 list-disc">
                                <Paragraph size="small">{`${t('eshop.transport.mtplInsuranceValidUntil')} ${format(mtplValidity.validUntil ?? '', 'yyyy-MM-dd')}`}</Paragraph>
                            </li>
                            <li className="ml-5 list-disc">
                                <Paragraph size="small">
                                    {t('eshop.transport.selectTwelveMonthsForCascoMessage')}
                                </Paragraph>
                            </li>
                        </ul>
                    )}
                </div>
            </Message>
        );
    }

    if (insuranceDuration !== InsuranceDuration.TwelveMonths) {
        return (
            <Message className="md:flex-row md:items-start" variant="info">
                <div className="flex flex-col gap-2">
                    <Paragraph size="small">{t('eshop.transport.selectTwelveMonthsForCascoMessage')}</Paragraph>
                </div>
            </Message>
        );
    }

    return null;
}
